import { action, computed, makeObservable, observable } from 'mobx';
import { checkFile, CheckFileOptions, FileAndErrors, FileErrorTypes, FileTypes } from '@app/domain/files';
import { mapValues } from '@app/utils/utils';
import {
  guestSession,
  SupplierRegistrationGuestSessionStore,
} from '@supplierRegistration/services/supplierRegistrationGuestSessionService';

export enum SupplierDocumentType {
  bankAccountDocumentation = 'bankAccountDocumentation',
  taxIdCertificate = 'taxIdCertificate',
}

const FILE_ERRORS_I18N_NAMESPACE = 'supplierValidation.supplierRegister.documents.fileErrors';

// checkFile() itself is un-translated (the shared getFileErrorText in fileUploadUtils.tsx renders
// hardcoded English for the mortee admin upload UIs) -- this feature maps every FileErrorTypes
// member checkFile() can produce, under DOCUMENT_FILE_OPTIONS below, onto its own i18n key instead.
// The Record forces every enum member to be covered, so a future FileErrorTypes addition fails to
// compile here rather than silently rendering an untranslated row. Rows are translated where they
// render (DocumentFileRow), not here -- this store only ever holds keys, never display text.
const CLIENT_FILE_ERROR_KEY_BY_TYPE: Record<FileErrorTypes, string> = {
  [FileErrorTypes.emptyFile]: `${FILE_ERRORS_I18N_NAMESPACE}.emptyFile`,
  [FileErrorTypes.fileNameLength]: `${FILE_ERRORS_I18N_NAMESPACE}.fileNameLength`,
  [FileErrorTypes.fileSize]: `${FILE_ERRORS_I18N_NAMESPACE}.fileSize`,
  [FileErrorTypes.invalidFileName]: `${FILE_ERRORS_I18N_NAMESPACE}.invalidFileName`,
  [FileErrorTypes.forbiddenExtension]: `${FILE_ERRORS_I18N_NAMESPACE}.forbiddenExtension`,
  [FileErrorTypes.tooManyFiles]: `${FILE_ERRORS_I18N_NAMESPACE}.tooManyFiles`,
  [FileErrorTypes.unknownExtension]: `${FILE_ERRORS_I18N_NAMESPACE}.unknownExtension`,
};

// The 422 per-file rejection body's errorCode (see SupplierValidationDocumentsRejectedDTO on the
// server) -- kept as a plain string rather than a union, since an unrecognized future code should
// degrade to the generic key (getServerFileErrorKey) rather than fail to compile.
const SERVER_FILE_ERROR_KEY: Record<string, string> = {
  infected: `${FILE_ERRORS_I18N_NAMESPACE}.infected`,
  disallowedContentType: `${FILE_ERRORS_I18N_NAMESPACE}.disallowedContentType`,
  tooLarge: `${FILE_ERRORS_I18N_NAMESPACE}.tooLarge`,
};

function getServerFileErrorKey(errorCode: string): string {
  return SERVER_FILE_ERROR_KEY[errorCode] ?? `${FILE_ERRORS_I18N_NAMESPACE}.unknown`;
}

// rowId identifies a row for the life of this store (React keys in DocumentUploadSection) --
// indices shift on every delete, and a File object can legitimately appear in two rows.
export interface AttachedDocumentFile extends FileAndErrors {
  rowId: number;
}

export interface DocumentServerFileError {
  type: SupplierDocumentType;
  // Position among the *valid* files of this type that were actually sent to the server (client-
  // errored rows are never transmitted) -- not the row's position in filesByType[type]. See
  // attachServerFileErrors for how this gets translated back to a row.
  index: number;
  errorCode: string;
}

// Single source of truth for what addFile validates against; the upload UI derives its accept
// attribute and cap from these values.
export const DOCUMENT_FILE_OPTIONS: CheckFileOptions = {
  maxFileSizeMB: 2,
  allowedExtensions: [FileTypes.pdf, FileTypes.jpg, FileTypes.jpeg, FileTypes.png],
  maxFilesAllowed: 3,
};

/**
 * Holds per-document-type attached files entirely outside antd form state. The Company/Individual
 * registration-type toggle resets all form fields via form.resetFields() -- uploaded files must
 * survive that reset, so they live here instead of as a form field/value.
 *
 * The cap in DOCUMENT_FILE_OPTIONS.maxFilesAllowed is enforced per type (filesByType[type].length),
 * so a full bank-documentation list never blocks a tax-certificate upload. checkFile doesn't know
 * about "too many files" -- it validates one file at a time -- so addFile checks the current count
 * itself and maps it onto this feature's own i18n key (CLIENT_FILE_ERROR_KEY_BY_TYPE) the same way
 * every other checkFile() result is mapped. A rejected file is still pushed with its errors attached
 * (there is no other way to observe a rejection through this store's public shape), the same way
 * FileAndErrors consumers elsewhere in this codebase keep invalid entries visible rather than
 * dropping them silently.
 *
 * verifiedEmail is a read-through getter to the guest session store, not a duplicated observable --
 * this store is where UI code should read it from, but the session store remains the single owner
 * of the underlying value. It is read-only: it updates automatically as the session store's value
 * changes and cannot be assigned through this store.
 */
export default class SupplierRegistrationDocumentsStore {
  @observable filesByType: Record<SupplierDocumentType, AttachedDocumentFile[]> = {
    [SupplierDocumentType.bankAccountDocumentation]: [],
    [SupplierDocumentType.taxIdCertificate]: [],
  };

  @observable submitProgressPercent: number | undefined = undefined;

  private nextRowId = 0;

  private readonly _guestSession: Pick<SupplierRegistrationGuestSessionStore, 'verifiedEmail'>;

  constructor(guestSessionStore: Pick<SupplierRegistrationGuestSessionStore, 'verifiedEmail'> = guestSession) {
    this._guestSession = guestSessionStore;
    makeObservable(this);
  }

  get verifiedEmail(): string | undefined {
    return this._guestSession.verifiedEmail;
  }

  @computed
  get hasAnyFiles(): boolean {
    return Object.values(this.filesByType).some((files) => files.length > 0);
  }

  // Files eligible to actually be submitted -- excludes rows carrying client-side validation
  // errors (too large, wrong extension, too many files, ...), which were never eligible in the
  // first place. This (not hasAnyFiles) is what the submit endpoint choice and the multipart
  // FormData contents must be keyed on.
  @computed
  get validFilesByType(): Record<SupplierDocumentType, File[]> {
    return mapValues(this.filesByType, (entries) => entries.filter((entry) => !entry.errors?.length).map((entry) => entry.file));
  }

  @computed
  get hasAnyValidFiles(): boolean {
    return Object.values(this.validFilesByType).some((files) => files.length > 0);
  }

  // Backs the "Continue without documents?" skip-confirmation gate: true when at least one
  // document type has zero *valid* files. Deliberately keyed on validFilesByType, not filesByType --
  // a section holding only client- or server-rejected rows has nothing eligible to submit and must
  // prompt exactly like a section nobody ever attached anything to, or a retry after a 422 would
  // silently complete the registration with that type's documents missing and no confirmation.
  @computed
  get hasAnyEmptyDocumentSection(): boolean {
    return Object.values(this.validFilesByType).some((files) => files.length === 0);
  }

  addFile = action((type: SupplierDocumentType, file: File): void => {
    const errors = checkFile(file, DOCUMENT_FILE_OPTIONS)
      // checkFile's filename charset rule (Latin+Hebrew) was written for the internal admin upload
      // UIs. Suppliers here name files in any of the portal's 25 locales, and the server supports
      // that deliberately: it stores the name untouched and serves it back RFC 5987-encoded
      // (filename*=UTF-8''...), so the charset restriction must not apply to this feature.
      .filter((errorType) => errorType !== FileErrorTypes.invalidFileName)
      .map((errorType) => CLIENT_FILE_ERROR_KEY_BY_TYPE[errorType]);

    this.filesByType[type].push({ file, errors, rowId: this.nextRowId++ });
    this.recalculateCapErrors(type);
  });

  removeFile = action((type: SupplierDocumentType, index: number): void => {
    this.filesByType[type].splice(index, 1);
    this.recalculateCapErrors(type);
  });

  // Cap slots are positional (row index >= maxFilesAllowed): the `multiple` file input can
  // overshoot the cap in one dialog, and a later delete must free the slots it opened up --
  // a tooManyFiles stamp left from add time would keep the row out of validFilesByType (silently
  // dropping it from the submit) while displaying a no-longer-true error. Only the cap key is
  // touched; size/extension/server errors on the same row are preserved.
  private recalculateCapErrors(type: SupplierDocumentType): void {
    const capErrorKey = CLIENT_FILE_ERROR_KEY_BY_TYPE[FileErrorTypes.tooManyFiles];

    this.filesByType[type].forEach((entry, index) => {
      const hasCapError = !!entry.errors?.includes(capErrorKey);
      const shouldHaveCapError = index >= DOCUMENT_FILE_OPTIONS.maxFilesAllowed;

      if (shouldHaveCapError && !hasCapError) {
        entry.errors = [...(entry.errors ?? []), capErrorKey];
      } else if (!shouldHaveCapError && hasCapError) {
        entry.errors = entry.errors?.filter((errorKey) => errorKey !== capErrorKey) ?? [];
      }
    });
  }

  setSubmitProgressPercent = action((percent: number | undefined): void => {
    this.submitProgressPercent = percent;
  });

  // Batched deliberately: every error's sent-index must be resolved against the *same* snapshot of
  // "which rows were valid" (computed once, up front). Attaching errors one at a time would corrupt
  // later lookups, since turning a valid row into an errored one shifts what "valid" means for the
  // remaining, not-yet-attached errors.
  attachServerFileErrors = action((errors: DocumentServerFileError[]): void => {
    const validIndicesByType = mapValues(this.filesByType, (entries: FileAndErrors[]) =>
      entries.reduce((validIndices: number[], entry, i) => {
        if (!entry.errors?.length) {
          validIndices.push(i);
        }
        return validIndices;
      }, [] as number[]),
    );

    errors.forEach(({ type, index, errorCode }) => {
      const originalIndex = validIndicesByType[type][index];

      if (originalIndex === undefined) {
        return;
      }

      const entry = this.filesByType[type][originalIndex];
      entry.errors = [...(entry.errors ?? []), getServerFileErrorKey(errorCode)];
    });
  });
}
